import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoiceCountReport } from "../../../state/reducers/invoiceCountReportSlice"; // Import the slice action
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const FullInvoiceChart = () => {
  const dispatch = useDispatch();
  const { data, status, error } = useSelector(
    (state) => state.invoiceCountReport
  ); // Updated the state name
  const [groupBy, setGroupBy] = useState("month"); // Default grouping: month
  const [reportType, setReportType] = useState("Analytics"); // Default report type: Analytics
  const [generateClicked, setGenerateClicked] = useState(false);

  useEffect(() => {
    dispatch(fetchInvoiceCountReport(groupBy)); // Fetch data for the selected group (month/year)
  }, [dispatch, groupBy]);

  const getChartData = () => {
    if (status === "succeeded" && data) {
      const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      let labels = months;
      let invoiceCounts = new Array(12).fill(0); // Default to zero for all months

      if (groupBy === "month" && data.monthly_invoice_count) {
        data.monthly_invoice_count.forEach((record) => {
          record.monthly_invoice_count.forEach((monthData) => {
            const monthIndex = months.indexOf(monthData.month);
            if (monthIndex !== -1) {
              invoiceCounts[monthIndex] = monthData.invoice_count;
            }
          });
        });

        return {
          labels,
          datasets: [
            {
              label: "Monthly Invoice Count",
              data: invoiceCounts,
              backgroundColor: "#1e40af", // Dark Blue color
            borderColor: "#00008B", // Dark Blue border
              borderWidth: 1,
            },
          ],
        };
      }

      if (groupBy === "year" && data.yearly_invoice_count) {
        const labels = data.yearly_invoice_count.map((item) => item.year);
        const invoiceCounts = data.yearly_invoice_count.map((item) => item.invoice_count);

        return {
          labels,
          datasets: [
            {
              label: "Yearly Invoice Count",
              data: invoiceCounts,
              backgroundColor: "#1e40af", // Dark Blue color
            borderColor: "#00008B", // Dark Blue border
              borderWidth: 1,
            },
          ],
        };
      }
    }

    return {
      labels: [],
      datasets: [
        {
          label: "No Data Available",
          data: [],
        },
      ],
    };
  };

  const handleGenerate = () => {
    setGenerateClicked(true); // Set flag to true when Generate is clicked
  };

  const renderList = () => {
    if (generateClicked && status === "succeeded" && data) {
      let invoiceData = [];

      if (groupBy === "month" && data.monthly_invoice_count) {
        invoiceData = data.monthly_invoice_count.flatMap((yearData) =>
          yearData.monthly_invoice_count.map((monthData) => ({
            month: monthData.month,
            invoice_count: monthData.invoice_count,
          }))
        );
      } else if (groupBy === "year" && data.yearly_invoice_count) {
        invoiceData = data.yearly_invoice_count.map((yearData) => ({
          year: yearData.year,
          invoice_count: yearData.invoice_count,
        }));
      }

      if (invoiceData.length === 0) {
        return <div className="text-center text-gray-500">No data available</div>;
      }

      return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">Invoice Count Data</h3>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Month/Year</th>
                <th className="px-4 py-2 text-left">Invoice Count</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-2">{item.month || item.year}</td>
                  <td className="px-4 py-2">{item.invoice_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="text-center text-gray-500">No data available</div>;
  };

  const chartData = getChartData();

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Fully Paid Invoice Count Report</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setGroupBy("month")}
            className={`px-4 py-2 rounded-md ${groupBy === "month" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setGroupBy("year")}
            className={`px-4 py-2 rounded-md ${groupBy === "year" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Yearly
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex gap-4">
          <select
            className="px-4 py-2 rounded-md"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="Analytics">Analytics</option>
            <option value="List">List</option>
          </select>
        </div>

        <div>
          <button
            onClick={handleGenerate}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Generate
          </button>
        </div>
      </div>

      {status === "loading" && <p className="text-gray-500">Loading...</p>}
      {status === "failed" && <p className="text-red-500">{error}</p>}

      {reportType === "Analytics" ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: true },
            },
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      ) : (
        renderList()  // Show table when report type is 'List'
      )}
    </div>
  );
};

export default FullInvoiceChart;
